"use client";

import { useMemo } from "react";
import {
  evaluatePricingRules,
  validateTicketConstraints,
  evaluateCartPricingRules,
  evaluatePriorPurchaseDisplayState,
  scalesWithSessions,
} from "../../../lib/pricing";
// Single source of truth for the balance-due-date rule + discount math — shared
// with the server so the checkout shows the exact date + split it charges/stamps.
import {
  resolveBalanceDueDate,
  allocateLineDiscounts,
  sumInstalmentShares,
  isInstalmentPlanConfig,
  computeLineDepositSplit,
  resolveInstalmentDueDate,
  type DiscountBaseItem,
} from "@obby/lib";

// Stable empty map so discount-free renders don't churn the memo identity.
const EMPTY_INSTALMENT_SHARES: ReadonlyMap<string, number[]> = new Map();
import { deriveChargeCadence } from "../../../lib/pricing/derive-cadence";
import { formatPolicyHint } from "../utils/format-policy-hint";
import type {
  ChargeCadence,
  PricingContext,
  PricingResult,
  CartPricingContext,
  CartPricingResult,
  CartItem,
  AppliedRule,
  DepositChargeConfig,
  PwywChargeConfig,
  ValidationError as V2ValidationError,
} from "../../../lib/pricing";
import type { TicketType, TicketSelection } from "../utils/types";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";

type UseV2PricingProps = {
  enabled: boolean;
  ticketTypesV2: any[];
  ticketSelections: TicketSelection[];
  selectedSession: any | null;
  rawSessionTicketTypes: any[];
  numberOfSessions: number;
  courseTimezone: string;
  /** `excludeAddOns` (DEV-327): the code leaves add-ons at full price. */
  discount: { value: number; type: string; excludeAddOns?: boolean } | null;
  addOnsTotal: number;
  bundleID?: string;
  customerChosenPrices?: Record<string, number>;
  selectedSessions?: Array<{ date: Date | string }>;
  cartPricingRules?: any[];
  /** IDs of ticket types the current buyer has already purchased for this
   *  course. Drives locked/hidden display state for tickets gated by
   *  REQUIRES_PRIOR_PURCHASE constraints. */
  priorPurchasedTicketIds?: string[];
  /** See CourseWithBundlesResponse.customerBookingHistory. Undefined => history
   *  conditions fail closed in the preview (anonymous buyer sees base price). */
  customerBookingHistory?: CustomerBookingHistory;
};

type V2PricingResultItem = {
  ticketTypeId: string;
  quantity: number;
  result: PricingResult;
  numberOfSessions: number;
  chargeType: string;
  /** Resolved charge cadence — see ChargeCadence. Drives whether the line
   *  total scales with quantity and/or session count. */
  cadence: ChargeCadence;
  depositAmount: number | null;
  depositRemainder: number | null;
  depositRemainderDue: string | null;
  /** How the balance is collected for a deposit ticket: emailed link vs paid in
   *  person. `null` for non-deposit lines. */
  remainderCollection: "payment_link" | "in_person" | null;
  /** Resolved balance due date (ISO string) for a deposit ticket, computed the
   *  same way the server does (resolveBalanceDueDate against the representative
   *  session), so the checkout shows the date that lands on the Booking.
   *  `null` for non-deposit lines or when the mode has no scheduled date. */
  balanceDueDate: string | null;
  /** Deposit lines only: the line's deposit due now and its full contract,
   *  pence, quantity- and session-scaled. The SAME split the server charges
   *  (`computeLineDepositSplit` off the line total, fixed amounts on charged
   *  tickets only). Prefer these over any per-unit field × quantity: a tiered
   *  line has no single per-unit price. `null` for non-deposit lines. */
  depositLinePence: number | null;
  depositContractPence: number | null;
  /** Per-unit deposit and full-price figures, pre-scaled by session count:
   *  the line figures above ÷ quantity. Exact on a uniform-price line; on a
   *  tiered line they are averages (possibly fractional pence), so totals
   *  must come from the line figures. `null` for non-deposit lines. */
  depositUnit: number | null;
  fullUnit: number | null;
  /** N-instalment payment plan preview (native `instalmentPlan` tickets only —
   *  legacy 2-leg deposits stay `null`). One entry per parcel. `amountLinePence`
   *  is the row's amount for the whole line, the same row the server
   *  schedules; `amountUnitPence` is that ÷ quantity (same scaling as
   *  `depositUnit`, an average on a tiered line). `dueDate` follows the
   *  single-session rule (null on multi-date/term, where one date would
   *  misrepresent later sessions). */
  instalmentPreview: Array<{
    label: string | null;
    amountLinePence: number;
    amountUnitPence: number;
    dueType: string;
    dueDate: string | null;
    collection: "payment_link" | "in_person";
    offlineNote: string | null;
  }> | null;
  /** Legacy alias for `cadence === "flat"`. Kept so existing consumers
   *  (cart total + deposit math) read it without churn. */
  isFlatPerOrder: boolean;
  /** Legacy alias for `cadence === "per_course"`. */
  isFlatPerCourse: boolean;
  /** Authoritative line total in minor units across all selected sessions and
   *  ticket quantity. In multi-date checkouts each session is evaluated against
   *  its own day-of-week / time / date context, so this can differ from
   *  `result.totalPerOrder × numberOfSessions` when a rule produces different
   *  per-session prices (e.g. a Friday-only override). Consumers should prefer
   *  this field over manual multiplication. */
  lineTotal: number;
  /** Per-session line totals for multi-date checkouts (length matches the
   *  selectedSessions array passed to the hook). Each entry is the rule-adjusted
   *  total for that session × ticket quantity. `undefined` for single-date and
   *  term-based flows — consumers should treat `lineTotal` as the single
   *  authoritative figure in those cases. */
  perSessionLineTotals?: number[];
  /** Per-session pricing breakdown for multi-date checkouts. Used by the
   *  checkout summary to split one ticket selection into multiple display
   *  rows when different sessions resolve to different prices/rules (e.g.
   *  Thursday £72.50 + Friday £50 → two rows). `undefined` for single-date
   *  and term-based — consumers fall back to a single row using `result`. */
  perSessionResults?: Array<{
    lineTotal: number;
    finalPrice: number;
    appliedRules: AppliedRule[];
  }>;
};

const EMPTY_TICKET_TYPES: TicketType[] = [];
const EMPTY_PRICING_RESULTS: V2PricingResultItem[] = [];
const EMPTY_ERRORS: V2ValidationError[] = [];

function resolvePwywPrice(
  basePrice: number,
  config: PwywChargeConfig,
  customerChosenPrice?: number,
): number {
  const chosen = customerChosenPrice ?? config.suggestedPrice;
  const min = config.allowZero ? 0 : config.minPrice;
  const max = config.maxPrice ?? Infinity;
  return Math.max(min, Math.min(chosen, max));
}

/**
 * V2 pricing hook — encapsulates all Ticket V2 pricing logic.
 *
 * Charge types determine the fundamental payment flow:
 *  - standard: full price at checkout
 *  - deposit: pay deposit now, remainder later (config on ticket)
 *  - pay_what_you_want: customer chooses price within bounds
 *  - free: £0
 *
 * Pricing rules (Early Bird, Volume, etc.) still apply on top of the base price
 * for standard and deposit charge types.
 */
export function useV2Pricing({
  enabled,
  ticketTypesV2,
  ticketSelections,
  selectedSession,
  rawSessionTicketTypes,
  numberOfSessions,
  courseTimezone,
  discount,
  addOnsTotal,
  bundleID,
  customerChosenPrices,
  selectedSessions,
  cartPricingRules,
  priorPurchasedTicketIds,
  customerBookingHistory,
}: UseV2PricingProps) {
  const ticketTypes = useMemo((): TicketType[] => {
    if (!enabled || ticketTypesV2.length === 0) return EMPTY_TICKET_TYPES;

    // Mirror V1's filter (use-checkout-regular.ts:244-251). When the
    // selected session has any ticket configuration, restrict the visible
    // V2 tickets to those configured on the session with status "enabled".
    // When the session has no ticketTypes at all (legacy pre-V2 sessions),
    // fall back to the full list — the hard gate in
    // CalculateV2Price.service.ts rejects any booking attempt for an
    // unlinked ticket regardless.
    const filteredV2 =
      rawSessionTicketTypes.length > 0
        ? ticketTypesV2.filter((tt: any) => {
            const stt = rawSessionTicketTypes.find(
              (s: any) => s.ticketTypeId === tt.id,
            );
            return stt && stt.status === "enabled";
          })
        : ticketTypesV2;

    // Drop hidden tickets — belt-and-braces, the server already strips
    // tickets whose REQUIRES_PRIOR_PURCHASE constraints resolve to "hidden".
    const purchased = priorPurchasedTicketIds ?? [];
    const eligibleV2 = filteredV2.filter(
      (tt: any) =>
        evaluatePriorPurchaseDisplayState(tt, filteredV2, purchased).state !== "hidden",
    );

    return eligibleV2.map((tt: any) => {
      const priorDisplay = evaluatePriorPurchaseDisplayState(tt, filteredV2, purchased);
      const sessionTicketType = rawSessionTicketTypes.find(
        (stt: any) => stt.ticketTypeId === tt.id,
      );

      let displayPrice = tt.price;
      if (tt.chargeType === "free") displayPrice = 0;
      if (tt.chargeType === "pay_what_you_want" && tt.chargeConfig) {
        displayPrice = (tt.chargeConfig as PwywChargeConfig).suggestedPrice ?? tt.price;
      }

      const cadence = deriveChargeCadence(tt);
      const sessionMult = scalesWithSessions(cadence) ? numberOfSessions : 1;

      return {
        id: tt.id,
        name: tt.name,
        description: tt.description,
        price: displayPrice,
        effectivePrice: Math.ceil(displayPrice * sessionMult),
        maxQuantity: tt.maxTicketsPerOrder ?? Infinity,
        minQuantity: tt.minTicketsPerOrder ?? 0,
        soldOut: false,
        numberOfGuestsPerTicket: tt.affectsAvailability ? 1 : 0,
        // Soft age range (booking seats Phase 5): warns, never blocks.
        minAge: tt.minAge ?? null,
        maxAge: tt.maxAge ?? null,
        // Carried so the picker can share one pool's cap across every ticket
        // type bound to it. This literal has no spread, so a field omitted
        // here simply never reaches the picker — which is exactly how the
        // cross-ticket cap shipped as dead code.
        resourcePoolId: tt.resourcePoolId ?? null,
        resourceUnitsPerTicket: tt.resourceUnitsPerTicket ?? 1,
        maxLimit: sessionTicketType?.maxLimit ?? undefined,
        currentSold: sessionTicketType?.currentSold ?? 0,
        // Per-ticket sale window: false once "now" is within
        // `availableUntilHoursBefore` hours of the selected session's start.
        // The ticket step renders isInTimeConstraint === false as unavailable.
        isInTimeConstraint:
          tt.availableUntilHoursBefore != null &&
          tt.availableUntilHoursBefore > 0 &&
          selectedSession?.startDateTime
            ? new Date().getTime() <
              new Date(selectedSession.startDateTime).getTime() -
                tt.availableUntilHoursBefore * 3_600_000
            : undefined,
        typeCharge: sessionMult > 1
          ? ("perPersonPerLesson" as const)
          : ("perPerson" as const),
        chargeType: tt.chargeType ?? "standard",
        chargeConfig: tt.chargeConfig ?? null,
        bookingPolicyId: tt.bookingPolicy?.id ?? tt.bookingPolicyId ?? null,
        bookingPolicy: tt.bookingPolicy ?? null,
        policyHint: formatPolicyHint(tt.bookingPolicy),
        pricingRules: tt.pricingRules ?? [],
        eligibilityMessage:
          priorDisplay.state === "locked" ? priorDisplay.message : undefined,
      };
    });
  }, [
    enabled,
    ticketTypesV2,
    rawSessionTicketTypes,
    numberOfSessions,
    priorPurchasedTicketIds,
    selectedSession,
  ]);

  const pricingResults = useMemo((): V2PricingResultItem[] => {
    if (!enabled || ticketSelections.length === 0 || !selectedSession)
      return EMPTY_PRICING_RESULTS;

    const totalTicketsInCart = ticketSelections.reduce(
      (sum, sel) => sum + sel.quantity,
      0,
    );

    // Build a session context per chosen date so date/time-aware pricing rules
    // (DAY_OF_WEEK, TIME_OF_DAY, PEAK_TIME, EARLY_BIRD, LAST_MINUTE,
    // DATE_RANGE, SPECIFIC_DATES, BOOKING_ADVANCE, MONTH_RANGE) evaluate per
    // session in multi-date checkouts. When only a single representative
    // session is available (regular single-date checkout, term-based courses)
    // we fall back to one context derived from `selectedSession` and rely on
    // the `numberOfSessions` multiplier downstream.
    const fallbackStart = new Date(selectedSession.startDateTime);
    type SessionCtx = {
      sessionStartTime: Date;
      sessionDayOfWeek: number;
      sessionTimeOfDay: string;
    };
    const toSessionCtx = (d: Date): SessionCtx => ({
      sessionStartTime: d,
      sessionDayOfWeek: d.getDay(),
      sessionTimeOfDay: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    });
    const sessionCtxs: SessionCtx[] =
      selectedSessions && selectedSessions.length > 1
        ? selectedSessions.map((s) => toSessionCtx(new Date(s.date)))
        : [toSessionCtx(fallbackStart)];
    const isMultiDate = sessionCtxs.length > 1;

    return ticketSelections
      .map((sel) => {
        const v2Ticket = ticketTypesV2.find(
          (tt: any) => tt.id === sel.ticketTypeId,
        );
        if (!v2Ticket) return null;

        const ct: string = v2Ticket.chargeType ?? "standard";

        let basePrice = v2Ticket.price;
        if (ct === "free") basePrice = 0;
        if (ct === "pay_what_you_want" && v2Ticket.chargeConfig) {
          basePrice = resolvePwywPrice(
            v2Ticket.price,
            v2Ticket.chargeConfig as PwywChargeConfig,
            customerChosenPrices?.[sel.ticketTypeId],
          );
        }

        // Resolve cadence. Non-standard charge types (free, deposit, pwyw)
        // ignore cadence — their own pricing semantics override the multiplier
        // matrix. Standard tickets read chargeCadence with a fallback to the
        // legacy chargeConfig.flatPer* flags.
        const cadence: ChargeCadence =
          ct === "standard" ? deriveChargeCadence(v2Ticket) : "per_ticket_per_session";
        const isFlatPerOrder = cadence === "flat";
        const isFlatPerSession = cadence === "per_session";
        const isFlatPerCourse = cadence === "per_course";

        // Flat and per-session cadences skip the rule engine — quantity does
        // not multiply, so per-ticket rules (tiered, group_size, etc.) don't
        // apply meaningfully. per_course cadence still runs the rule engine
        // (quantity multiplies), the session multiplier is suppressed below.
        if (cadence === "flat" || cadence === "per_session") {
          const flatLabel =
            cadence === "per_session"
              ? sel.quantity > 1
                ? `Flat price per session (×${sel.quantity} included)`
                : "Flat price per session"
              : sel.quantity > 1
                ? `Flat price (×${sel.quantity} included)`
                : "Flat price";
          const flatResult: PricingResult = {
            basePrice,
            finalPrice: basePrice,
            totalPerOrder: basePrice,
            depositAmount: null,
            depositRemainder: null,
            depositRemainderDue: null,
            appliedRules: [],
            breakdown: [
              { label: flatLabel, amount: basePrice, type: "base" },
              { label: "Total", amount: basePrice, type: "total" },
            ],
            isSlidingScale: false,
            slidingScaleConfig: null,
          };
          // per_session: scale by the actual session count. For multi-date
          // checkouts (regular flow with multiple selected dates) that's
          // `sessionCtxs.length`; for single-date / term flows fall back to
          // `numberOfSessions` (which is the term's session count). Mirrors
          // the rule-engine path's `scalesWithSessions(cadence)` multiplier
          // and matches the server's `sessionsCount` math in CalculateV2Price.
          const flatLineTotal =
            cadence === "per_session"
              ? basePrice * (isMultiDate ? sessionCtxs.length : numberOfSessions)
              : basePrice;
          return {
            ticketTypeId: sel.ticketTypeId,
            quantity: sel.quantity,
            result: flatResult,
            numberOfSessions,
            chargeType: ct,
            cadence,
            depositAmount: null,
            depositRemainder: null,
            depositRemainderDue: null,
            remainderCollection: null,
            balanceDueDate: null,
            depositLinePence: null,
            depositContractPence: null,
            depositUnit: null,
            fullUnit: null,
            instalmentPreview: null,
            isFlatPerOrder,
            isFlatPerCourse: false,
            lineTotal: flatLineTotal,
          };
        }

        const sessionsCountForRules =
          selectedSessions && selectedSessions.length > 0
            ? selectedSessions.length
            : numberOfSessions;

        const pricingRules = (v2Ticket.pricingRules ?? []).filter(
          (r: any) => r.ruleType !== "DEPOSIT" && r.ruleType !== "SLIDING_SCALE",
        );

        // Evaluate the rule engine once per session context. In the
        // single-date and term-based paths there is exactly one context, so
        // this collapses to the previous behaviour. In multi-date checkouts
        // each session is evaluated against its own day-of-week / time /
        // date — rules like "£50 on Fridays" can then differ per session.
        const perSessionResults = sessionCtxs.map((sctx) => {
          const context: PricingContext = {
            ticketType: v2Ticket,
            quantity: sel.quantity,
            totalTicketsInCart,
            sessionsCount: sessionsCountForRules,
            sessionStartTime: sctx.sessionStartTime,
            bookingTime: new Date(),
            sessionDayOfWeek: sctx.sessionDayOfWeek,
            sessionTimeOfDay: sctx.sessionTimeOfDay,
            customerTags: [],
            customerPreviousBookings: customerBookingHistory?.previousBookings,
            customerBookingsByPeriod: customerBookingHistory?.bookingsByPeriod,
            schoolTimezone: courseTimezone,
            selectedSessions,
          };
          return evaluatePricingRules(basePrice, pricingRules, context);
        });

        // Representative result for display (rule names, breakdown, finalPrice
        // for headline display). The aggregate `lineTotal` is the authoritative
        // line total — consumers should use it rather than multiplying
        // totalPerOrder by numberOfSessions.
        const result = perSessionResults[0]!;

        // Authoritative line total across all selected sessions × quantity.
        // - Multi-date: sum each session's evaluated totalPerOrder.
        // - Single-date / term-based: one session × numberOfSessions multiplier
        //   (flatPerCourse caps to a single course-wide line).
        // Cadence-driven line total. `result.totalPerOrder` already equals
        // `finalPrice × quantity` (engine multiplies by quantity). When the
        // cadence scales with sessions (per_ticket_per_session, per_session),
        // multiply by numberOfSessions. When it doesn't (per_course, flat),
        // the rule-engine total is the whole line.
        const lineTotal = isMultiDate
          ? perSessionResults.reduce((s, r) => s + r.totalPerOrder, 0)
          : scalesWithSessions(cadence)
            ? result.totalPerOrder * numberOfSessions
            : result.totalPerOrder;

        // Per-session breakdown for multi-date — needed by the basket flow so
        // each session-item carries the right rule-adjusted total instead of
        // the cross-date aggregate or its average. Undefined for single-date /
        // term-based since one figure covers the line.
        const perSessionLineTotals = isMultiDate
          ? perSessionResults.map((r) => r.totalPerOrder)
          : undefined;
        const perSessionResultsExposed = isMultiDate
          ? perSessionResults.map((r) => ({
              lineTotal: r.totalPerOrder,
              finalPrice: r.finalPrice,
              appliedRules: r.appliedRules,
            }))
          : undefined;

        let ticketDepositAmount: number | null = null;
        let ticketDepositRemainder: number | null = null;
        let ticketDepositRemainderDue: string | null = null;
        let ticketRemainderCollection: "payment_link" | "in_person" | null =
          null;
        let ticketBalanceDueDate: string | null = null;
        let ticketDepositUnit: number | null = null;
        let ticketFullUnit: number | null = null;
        let ticketDepositLine: number | null = null;
        let ticketDepositContract: number | null = null;
        let ticketInstalmentPreview: V2PricingResultItem["instalmentPreview"] =
          null;

        // Deposit lines: the server's split, through the SAME shared helper
        // (computeLineDepositSplit), off the line's actual contract — never
        // finalPrice × quantity, which overstates a tiered line (its rule
        // prices the whole line; finalPrice is only the rounded average over
        // the tickets it charges). Fixed amounts count the CHARGED tickets:
        // tickets past a tiered cap are free and carry no deposit. Deposit
        // cadence is per_ticket_per_session, so the session multiplier is
        // `numberOfSessions`, as below for the per-unit fields.
        const depositContract = result.totalPerOrder * numberOfSessions;
        const lineUnits = Math.max(1, sel.quantity * numberOfSessions);
        const depositSplit =
          ct === "deposit" && v2Ticket.chargeConfig
            ? computeLineDepositSplit(v2Ticket.chargeConfig, {
                lineTotalPence: depositContract,
                unitPricePence: result.finalPrice,
                units: (result.chargedQuantity ?? sel.quantity) * numberOfSessions,
              })
            : null;
        if (depositSplit) {
          ticketDepositLine = depositSplit.depositPence;
          ticketDepositContract = depositContract;
          // Per-unit mirrors = the line ÷ its units: exact on a uniform line
          // (the figures these fields always carried), an average on a tiered one.
          ticketDepositAmount = depositSplit.depositPence / lineUnits;
          ticketDepositRemainder = depositSplit.remainderPence / lineUnits;
          ticketDepositUnit = ticketDepositAmount * numberOfSessions;
          ticketFullUnit = (depositContract / lineUnits) * numberOfSessions;
        }

        if (ct === "deposit" && v2Ticket.chargeConfig && isInstalmentPlanConfig(v2Ticket.chargeConfig)) {
          // ── Native N-instalment plan ──
          // Row amounts are the server's line-level schedule (depositSplit);
          // the legacy 2-leg fields above mirror row 0 / the rest so every
          // existing consumer keeps working.
          const plan = v2Ticket.chargeConfig.instalmentPlan;
          const lineAmounts = depositSplit?.rowAmounts ?? [];
          ticketDepositRemainderDue = plan.rows[1]?.dueType ?? null;
          // The NEXT payment's method drives the legacy disclosure sentence.
          ticketRemainderCollection =
            plan.rows[1]?.collection === "payment_link"
              ? "payment_link"
              : "in_person";
          // Single-session rule, same as the legacy branch below.
          const singleSession = !isMultiDate && numberOfSessions === 1;
          const purchaseDate = new Date();
          ticketInstalmentPreview = plan.rows.map((row, k) => {
            const due = singleSession
              ? resolveInstalmentDueDate(row, {
                  sessionStart: fallbackStart,
                  purchaseDate,
                })
              : null;
            return {
              label: row.label ?? null,
              amountLinePence: lineAmounts[k] ?? 0,
              amountUnitPence: ((lineAmounts[k] ?? 0) / lineUnits) * numberOfSessions,
              dueType: row.dueType,
              dueDate:
                due && !Number.isNaN(due.getTime()) ? due.toISOString() : null,
              collection:
                row.collection === "payment_link" ? "payment_link" : "in_person",
              offlineNote: row.offlineNote ?? null,
            };
          });
          ticketBalanceDueDate = ticketInstalmentPreview[1]?.dueDate ?? null;
        } else if (ct === "deposit" && v2Ticket.chargeConfig) {
          const cfg = v2Ticket.chargeConfig as DepositChargeConfig;
          ticketDepositRemainderDue = cfg.remainderDue;
          // Absent on legacy configs → the balance is collected in person.
          ticketRemainderCollection =
            cfg.remainderCollection === "payment_link"
              ? "payment_link"
              : "in_person";
          // Only resolve a concrete due date for a SINGLE-session booking, where
          // it's unambiguous and matches the server's per-item stamp. Multi-date
          // / term deposits span sessions on different dates, so one date would
          // misrepresent the later ones — leave it null (the card then shows the
          // amount + collection method without a date).
          const singleSession = !isMultiDate && numberOfSessions === 1;
          const due = singleSession
            ? resolveBalanceDueDate(cfg, fallbackStart)
            : null;
          ticketBalanceDueDate =
            due && !Number.isNaN(due.getTime()) ? due.toISOString() : null;
        }

        return {
          ticketTypeId: sel.ticketTypeId,
          quantity: sel.quantity,
          result,
          numberOfSessions,
          chargeType: ct,
          cadence,
          depositAmount: ticketDepositAmount,
          depositRemainder: ticketDepositRemainder,
          depositRemainderDue: ticketDepositRemainderDue,
          remainderCollection: ticketRemainderCollection,
          balanceDueDate: ticketBalanceDueDate,
          depositLinePence: ticketDepositLine,
          depositContractPence: ticketDepositContract,
          depositUnit: ticketDepositUnit,
          fullUnit: ticketFullUnit,
          instalmentPreview: ticketInstalmentPreview,
          isFlatPerOrder: false,
          isFlatPerCourse,
          lineTotal,
          perSessionLineTotals,
          perSessionResults: perSessionResultsExposed,
        };
      })
      .filter(Boolean) as V2PricingResultItem[];
  }, [
    enabled,
    ticketSelections,
    selectedSession,
    ticketTypesV2,
    numberOfSessions,
    courseTimezone,
    customerChosenPrices,
    selectedSessions,
    customerBookingHistory,
  ]);

  const cartPricingResult = useMemo((): CartPricingResult | null => {
    if (!enabled || !cartPricingRules?.length || pricingResults.length === 0 || !selectedSession)
      return null;

    const ticketsSubtotal = pricingResults.reduce((sum, item) => {
      if (item.chargeType === "free") return sum;
      return sum + item.lineTotal;
    }, 0);

    if (ticketsSubtotal <= 0) return null;

    const sessionStart = new Date(selectedSession.startDateTime);
    const dayOfWeek = sessionStart.getDay();
    const hours = sessionStart.getHours().toString().padStart(2, "0");
    const minutes = sessionStart.getMinutes().toString().padStart(2, "0");
    const timeOfDay = `${hours}:${minutes}`;

    const cart: CartItem[] = pricingResults.map((r) => ({
      ticketTypeId: r.ticketTypeId,
      quantity: r.quantity,
    }));

    const cartSessionsCountForRules =
      selectedSessions && selectedSessions.length > 0
        ? selectedSessions.length
        : numberOfSessions;

    const cartCtx: CartPricingContext = {
      cart,
      ticketTypes: ticketTypesV2,
      totalCartAmount: ticketsSubtotal,
      sessionsCount: cartSessionsCountForRules,
      selectedSessions,
      bookingTime: new Date(),
      customerTags: [],
      customerPreviousBookings: customerBookingHistory?.previousBookings,
      customerBookingsByPeriod: customerBookingHistory?.bookingsByPeriod,
      sessionStartTime: sessionStart,
      sessionDayOfWeek: dayOfWeek,
      sessionTimeOfDay: timeOfDay,
      schoolTimezone: courseTimezone,
    };

    return evaluateCartPricingRules(ticketsSubtotal, cartPricingRules, cartCtx);
  }, [enabled, cartPricingRules, pricingResults, selectedSession, ticketTypesV2, numberOfSessions, selectedSessions, courseTimezone, customerBookingHistory]);

  const { total, discountAmount, totalRemainingBalance, instalmentDiscountShares } = useMemo(() => {
    const EMPTY = {
      total: 0,
      discountAmount: 0,
      totalRemainingBalance: 0,
      instalmentDiscountShares: EMPTY_INSTALMENT_SHARES,
    };
    if (bundleID) return EMPTY;
    if (!enabled || pricingResults.length === 0) return EMPTY;

    // Per-line bases: a deposit line carries its FULL contract (now + later);
    // everything else (non-deposit tickets, cart adjustment, add-ons) is now-only.
    // The discount is allocated through the shared @obby/lib line model
    // (allocateLineDiscounts) — the SAME allocation + per-line split the server
    // charges/stamps — so "Balance due now / later" matches to the penny.
    const lines: DiscountBaseItem[] = [];
    pricingResults.forEach((item, idx) => {
      if (item.chargeType === "free") return;
      if (item.depositLinePence !== null && item.depositContractPence !== null) {
        // The line-level split the server charges (see the pricingResults memo):
        // never per-unit × quantity, which overstates a tiered line.
        lines.push({
          id: `t${idx}`,
          isDeposit: true,
          coreBase: item.depositContractPence,
          depositNow: item.depositLinePence,
        });
      } else {
        lines.push({ id: `t${idx}`, coreBase: item.lineTotal });
      }
    });
    // The cart adjustment is a now-only line (negative for a cart discount: it
    // lowers what a code applies to without taking a share). The add-ons are
    // their own line, so a code that excludes them (DEV-327) leaves them at
    // full price. An add-on-free, code-free render prices exactly as before.
    const cartAdjustment = cartPricingResult?.cartAdjustment ?? 0;
    const addOns = Math.max(0, addOnsTotal);
    // A cart discount reaches the add-ons in proportion, as the server's
    // applyCartShare spreads it over the core and the add-ons; a surcharge
    // stays on the core (Ruling P6.7).
    const ticketsBase = lines.reduce((s, l) => s + Math.max(0, l.coreBase), 0);
    const addOnCartShare =
      cartAdjustment < 0 && addOns > 0 && ticketsBase + addOns > 0
        ? Math.max(-addOns, Math.round((cartAdjustment * addOns) / (ticketsBase + addOns)))
        : 0;
    if (cartAdjustment !== 0 || addOns > 0) {
      lines.push({
        id: "adj",
        coreBase: cartAdjustment - addOnCartShare,
        addOnPence: addOns + addOnCartShare,
      });
    }

    const grossTotal = lines.reduce(
      (s, l) => s + (l.isDeposit ? l.depositNow ?? 0 : l.coreBase) + (l.addOnPence ?? 0),
      0
    ); // charged now, pre-discount
    const laterSubtotal = lines.reduce(
      (s, l) => s + (l.isDeposit ? l.coreBase - (l.depositNow ?? 0) : 0),
      0
    );

    let nowDisc = 0;
    let laterDisc = 0;
    const rowShares = new Map<string, number[]>();
    if (discount) {
      const allocation = allocateLineDiscounts(lines, [
        {
          type: discount.type === "PERCENTAGE" ? "percentage" : "fixed",
          value: discount.type === "PERCENTAGE" ? Math.min(discount.value, 100) : discount.value,
          excludeAddOns: !!discount.excludeAddOns,
        },
      ]);
      const perItem = allocation.core;
      nowDisc = allocation.nowShare;
      laterDisc = allocation.laterShare;

      // Per-ROW discount shares for plan lines, keyed by ticketTypeId — the
      // schedule disclosure must show DISCOUNTED instalments or it contradicts
      // the discounted totals right beneath it (review finding #10).
      // sumInstalmentShares mirrors the server's applyInstalmentSplit
      // (splitAcrossInstalments both sides), so each displayed row equals what
      // that leg actually collects. Line-level amounts = the preview's
      // amountLinePence, the same figures the summary card renders.
      const planLines = pricingResults
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => (item.instalmentPreview?.length ?? 0) >= 2);
      if (planLines.length > 0) {
        const { perLine } = sumInstalmentShares(
          planLines.map(({ item, idx }) => ({
            id: `t${idx}`,
            amounts: (item.instalmentPreview ?? []).map((r) => r.amountLinePence),
          })),
          perItem
        );
        for (const { item, idx } of planLines) {
          const lineShares: number[] | undefined = perLine.get(`t${idx}`);
          if (lineShares?.some((s: number) => s > 0)) {
            rowShares.set(item.ticketTypeId, lineShares);
          }
        }
      }
    }

    return {
      total: Math.max(0, grossTotal - nowDisc),
      discountAmount: nowDisc,
      totalRemainingBalance: Math.max(0, laterSubtotal - laterDisc),
      instalmentDiscountShares: rowShares.size > 0 ? rowShares : EMPTY_INSTALMENT_SHARES,
    };
  }, [enabled, pricingResults, addOnsTotal, discount, bundleID, cartPricingResult]);

  const constraintErrors = useMemo((): V2ValidationError[] => {
    if (!enabled || ticketTypesV2.length === 0) return EMPTY_ERRORS;
    const hasTickets = ticketSelections.some((s) => s.quantity > 0);
    if (!hasTickets) return EMPTY_ERRORS;

    const cart: CartItem[] = ticketSelections.map((s) => ({
      ticketTypeId: s.ticketTypeId,
      quantity: s.quantity,
    }));
    const sessionStart = selectedSession?.startDateTime
      ? new Date(selectedSession.startDateTime)
      : undefined;
    const validation = validateTicketConstraints(cart, ticketTypesV2, undefined, selectedSessions, {
      bookingTime: new Date(),
      sessionStartTime: sessionStart,
      sessionDayOfWeek: sessionStart?.getDay(),
      sessionTimeOfDay: sessionStart
        ? `${String(sessionStart.getHours()).padStart(2, "0")}:${String(sessionStart.getMinutes()).padStart(2, "0")}`
        : undefined,
      schoolTimezone: courseTimezone,
      priorPurchasedTicketIds: priorPurchasedTicketIds ?? [],
    });
    return validation.isValid ? EMPTY_ERRORS : validation.errors;
  }, [
    enabled,
    ticketTypesV2,
    ticketSelections,
    selectedSessions,
    selectedSession,
    courseTimezone,
    priorPurchasedTicketIds,
  ]);

  const buildBookingData = useMemo(() => {
    if (!enabled) return () => ({});

    return () => ({
      ticketVersion: "v2" as const,
      ticketSelectionsV2: ticketSelections.map((sel) => ({
        ticketTypeId: sel.ticketTypeId,
        quantity: sel.quantity,
      })),
      pricingSummary: {
        lineItems: pricingResults.map((r) => ({
          ticketTypeId: r.ticketTypeId,
          ticketName:
            ticketTypesV2.find((tt: any) => tt.id === r.ticketTypeId)?.name ??
            "",
          chargeType: r.chargeType,
          basePrice: r.result.basePrice,
          finalPrice: r.result.finalPrice,
          quantity: r.quantity,
          numberOfSessions: r.numberOfSessions,
          appliedRules: r.result.appliedRules,
          breakdown: r.result.breakdown,
        })),
        // Line-level, like the server: ChargeCourse persists this
        // depositRemainder as the booking's balance when post-payment
        // re-pricing fails, so it must never be per-unit × quantity.
        depositAmount: pricingResults.some((r) => r.depositLinePence !== null)
          ? pricingResults.reduce((sum, r) => sum + (r.depositLinePence ?? 0), 0)
          : null,
        depositRemainder: pricingResults.some((r) => r.depositLinePence !== null)
          ? pricingResults.reduce(
              (sum, r) =>
                sum +
                (r.depositContractPence ?? 0) -
                (r.depositLinePence ?? 0),
              0,
            )
          : null,
        depositRemainderDue:
          pricingResults.find((r) => r.depositRemainderDue !== null)
            ?.depositRemainderDue ?? null,
      },
    });
  }, [enabled, ticketSelections, pricingResults, ticketTypesV2]);

  return {
    ticketTypes,
    pricingResults,
    total,
    discountAmount,
    totalRemainingBalance,
    /** Per-row discount share vectors for plan lines (ticketTypeId → pence per
     *  row, payment order, LINE level) — subtract from the schedule display. */
    instalmentDiscountShares,
    constraintErrors,
    buildBookingData,
    cartPricingResult,
  };
}

export type { V2PricingResultItem };
